import { CSSProperties } from 'react';
import { CloseButton, Stack, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useSelector } from 'react-redux';
import { selectBannerModalSettings } from '../../../redux/selectors/publicSelector';
import { BannerModalPosition } from '../../../models/setting.model';
import DOMPurify from 'dompurify';

const getPositionStyle = (position: BannerModalPosition): CSSProperties => {
  switch (position) {
    case BannerModalPosition.TOP_LEFT:
      return { top: '12vh', left: '2vw' };
    case BannerModalPosition.TOP_CENTER:
      return { top: '12vh', left: '50%', transform: 'translateX(-50%)' };
    case BannerModalPosition.TOP_RIGHT:
      return { top: '12vh', right: '2vw' };
    case BannerModalPosition.CENTER:
      return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
    case BannerModalPosition.BOTTOM_LEFT:
      return { bottom: '3vh', left: '2vw' };
    case BannerModalPosition.BOTTOM_CENTER:
      return { bottom: '3vh', left: '50%', transform: 'translateX(-50%)' };
    case BannerModalPosition.BOTTOM_RIGHT:
    default:
      return { bottom: '3vh', right: '2vw' };
  }
};

const PromoBannerModal = () => {
  const banner = useSelector(selectBannerModalSettings);
  const [opened, { close }] = useDisclosure(true);

  if (!banner.enabled || !opened || (!banner.title && !banner.text)) {
    return null;
  }

  return (
    <div
      style={{
        position: 'fixed',
        zIndex: 1100,
        maxWidth: 360,
        width: '85vw',
        backgroundColor: banner.backgroundColor,
        color: banner.textColor,
        border: `1px solid ${banner.borderColor}`,
        borderRadius: 12,
        maxHeight: '70vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        padding: '20px 24px',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.35)',
        ...getPositionStyle(banner.position),
      }}
    >
      <CloseButton
        onClick={close}
        aria-label="Close banner"
        style={{ position: 'absolute', top: 8, right: 8, zIndex: 1, color: banner.textColor }}
      />
      <div
        style={{
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          overscrollBehavior: 'contain',
          paddingTop: 8,
          paddingBottom: 8,
        }}
      >
        <Stack gap={6} align="center" ta="center">
          {banner.title && (
            <Text fw={700} style={{ color: banner.textColor, fontSize: banner.fontSize + 4 }}>
              {banner.title}
            </Text>
          )}
          {banner.text && (
            <div
              style={{ color: banner.textColor, fontSize: banner.fontSize }}
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(banner.text) }}
            />
          )}
        </Stack>
      </div>
    </div>
  );
};

export default PromoBannerModal;
